import { useMutation } from "@apollo/client";
import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Select,
  TimePicker,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { capitalize, isFunction } from "lodash";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_LEAD_TASK, UPDATE_LEAD_TASK } from "../../../graphql/mutation";
import { getMobileView } from "../../../redux/slices/isMobile";
import UserSelector from "../../commonComponents/userSelector";
import { UserContext } from "../../../amplify/authenticator-provider";
import { taskStatus } from "./engagementHelpers";
import { addTask, updateLeadTaskRedux } from "../../../redux/slices/taskSlice";
import { AccessPermissionAction, timezones } from "../../../library/constants";
import LinkIcon from "./icons/LinkIcon";
import { checkAccessPermission, openUrlInTab } from "../../../library/helpers";
import SuggestedAction from "../tasks/SuggestedAction";

/**
 *
 * @param {{
 * leadTaskData:{
 * open: Boolean,
 * leadTask: any,
 * selectedLead: any,
 * mode: "ADD" | "EDIT"
 * isCompleted:Boolean
 * },
 * setleadTaskData:()=>any,
 * refetchLeadTask:()=>any,
 * setLeadTasks:()=>any,
 * }} props
 * @returns
 */
export const TaskDrawer = props => {
  const dispatch = useDispatch();
  const canEditTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_ADD_EDIT);

  const { user } = useContext(UserContext);
  const { leadTaskData, setleadTaskData } = props;
  const [markedCompleted, setmarkedCompleted] = useState(false);
  const [formChanged, setformChanged] = useState(false);
  const { selectedLead } = useSelector(state => state.lead);
  const [form] = Form.useForm();
  const isMobileView = useSelector(state => getMobileView(state));
  const [assignedUsers, setassignedUsers] = useState(null);

  const getLocation = () => {
    return user?.locations?.find(
      item => item.location.id === sessionStorage.getItem("currentLocationId")
    );
  };
  const locationObj = useMemo(getLocation, []);
  const timeZone = sessionStorage.getItem("currentLocationId")?.includes(",")
    ? props?.selectedLead?.location?.timezone
    : locationObj?.location?.timezone;

  const getTimezoneLabel = () => {
    const obj = timezones.find(item => item.value === timeZone);
    if (obj) {
      return obj.label;
    }
    return timeZone;
  };
  const timeZoneLabel = useMemo(() => getTimezoneLabel(), timeZone);

  const crmIntegrationType = user?.company?.crmIntegrationType;

  const opportunities = useMemo(() => {
    return (
      leadTaskData?.selectedLead &&
      leadTaskData?.selectedLead?.leadOpportunities &&
      leadTaskData?.selectedLead?.leadOpportunities.length > 0 &&
      leadTaskData?.selectedLead?.leadOpportunities
    );
  }, [leadTaskData?.selectedLead && leadTaskData?.selectedLead?.leadOpportunities]);

  const handleLinkClick = leadId => {
    const host = window.location.origin;
    const url = `${host}/engagements/activity-center/?leadId=${leadId}&loc=${leadTaskData?.selectedLead?.locationId}`;
    openUrlInTab(url);
  };

  const title = useMemo(() => {
    const mode = capitalize(leadTaskData?.mode);
    const leadName = leadTaskData?.selectedLead?.fullName;
    const leadId = leadTaskData?.selectedLead?.id;

    return (
      <div className="flex flex-col">
        <Typography.Title className="!mb-0" level={5} strong>
          {mode} task
        </Typography.Title>
        <div className="flex items-center gap-2">
          <Typography.Text type="secondary" className="!text-[#000000A6] text-sm">
            Lead name: {leadName || "N/A"}
          </Typography.Text>
          <div className="mt-1 cursor-pointer" onClick={() => handleLinkClick(leadId)}>
            <LinkIcon />
          </div>
        </div>
      </div>
    );
  }, [leadTaskData?.mode, leadTaskData?.selectedLead?.fullName, leadTaskData?.selectedLead?.id]);

  useEffect(() => {
    if (leadTaskData.mode === "EDIT" && leadTaskData.open) {
      const { completionDate, completionNote, ...leadTask } = leadTaskData.leadTask;
      form.setFieldsValue({
        ...leadTask,
        dueDate: dayjs(leadTask.dueDate),
        assignedTo: +leadTask.assignedTo.id,
        dueTime: dayjs(leadTask.dueDate),
        ...(leadTask.taskStatus === "COMPLETED"
          ? {
              completionNote,
              completionDate: dayjs(completionDate),
              completionTime: dayjs(completionDate),
            }
          : {}),
      });
      setassignedUsers(+leadTaskData.leadTask.assignedTo.id);
      setmarkedCompleted(
        leadTaskData.leadTask.taskStatus === "COMPLETED" || leadTaskData.isCompleted
      );
    }
  }, [leadTaskData]);
  const [createLeadTask, { loading: createTaskLoader }] = useMutation(CREATE_LEAD_TASK);
  const [updateLeadTask, { loading: updateTaskLoader }] = useMutation(UPDATE_LEAD_TASK);
  const formatDateTime = (dateInput, timeInput) => {
    const date = dayjs(dateInput).format("YYYY-MM-DD");
    const time = dayjs(timeInput).format("HH:mm:ss.SSS");
    return dayjs(`${date}T${time}Z`).toISOString();
  };
  const handleClose = () => {
    setleadTaskData({ ...leadTaskData, open: false });
  };

  const discardDraft = () => {
    setleadTaskData({ ...leadTaskData, open: false });
    form.resetFields();
    setmarkedCompleted(false);
    setformChanged(false);
  };
  useEffect(() => {
    discardDraft();
  }, [selectedLead.id]);

  const handleSave = async values => {
    values = {
      ...values,
      assignedTo: assignedUsers,
      dueDate: formatDateTime(values.dueDate, values.dueTime),
      ...(markedCompleted
        ? { completionDate: formatDateTime(values.completionDate, values.completionTime) }
        : {}),
      leadId: leadTaskData.selectedLead.id,
      ...(markedCompleted ? { taskStatus: "COMPLETED" } : { taskStatus: "CREATED" }),
    };
    const { dueTime, ...payload } = values;
    if (leadTaskData.mode === "ADD") {
      const res = await createLeadTask({
        variables: { ...payload },
      });
      if (res.data.createLeadTask.statusCode === 200) {
        message.success(res.data.createLeadTask.message);
        dispatch(addTask(res.data.createLeadTask.leadTask));
        handleClose();
      }
    } else {
      const res = await updateLeadTask({
        variables: {
          ...payload,
          assignedTo: assignedUsers,
          id: +leadTaskData.leadTask.id,
        },
      });
      if (res.data.updateLeadTask.statusCode === 200) {
        message.success(res.data.updateLeadTask.message);
        dispatch(updateLeadTaskRedux(res.data.updateLeadTask.leadTask));
        handleClose();
        if (props?.handleRefetch && isFunction(props?.handleRefetch)) {
          props?.handleRefetch();
        }
      }
    }
  };

  const handleOpportunityName = oppr => {
    const opprId =
      crmIntegrationType === "DS" && oppr?.dsOpportunityEvents?.length > 0
        ? oppr?.dsOpportunityEvents[0].dsEventId
        : crmIntegrationType === "DT" && oppr?.dtLeadOpportunities?.length > 0
        ? oppr?.dtLeadOpportunities[0].dtOpportunityId
        : crmIntegrationType === "SF" && oppr?.sfLeadOpportunities?.length > 0
        ? oppr?.sfLeadOpportunities[0].sfOpportunityId
        : crmIntegrationType === "VIN" && oppr?.vsLeadOpportunities?.length > 0
        ? oppr?.vsLeadOpportunities[0].vsLeadId
        : crmIntegrationType === "EL" && oppr?.elLeadOpportunities?.length > 0
        ? oppr?.elLeadOpportunities[0].elOpportunityId
        : crmIntegrationType === "MF" && oppr?.mfLeadOpportunities?.length > 0
        ? oppr?.mfLeadOpportunities[0].mfLeadId
        : oppr?.id;
    return `${oppr?.name || ""} #${opprId || ""}`;
  };

  return (
    <Drawer
      width={isMobileView ? "100%" : "40%"}
      open={leadTaskData.open}
      title={title}
      footer={
        <div className="flex-row-centered justify-between">
          <div className="flex-row-centered gap-1">
            <Button
              loading={createTaskLoader || updateTaskLoader}
              onClick={() => form.submit()}
              type="primary"
              disabled={!canEditTask}
            >
              Save
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
            {formChanged && (
              <Button type="link" onClick={discardDraft}>
                Discard draft
              </Button>
            )}
          </div>
          {leadTaskData.mode === "EDIT" && (
            <div className="flex-col-centered gap-1">
              {leadTaskData.leadTask.taskStatus === "COMPLETED" && (
                <span className="flex-row-centered gap-1">
                  <span className="xs-400 text-grey-700">Completed by</span>
                  <span className="xs-400 text-grey-500">
                    {leadTaskData.leadTask.completedBy.fullName}
                  </span>
                </span>
              )}
              <span className="flex-row-centered gap-1">
                <span className="xs-400 text-grey-700">Created by:</span>
                <span className="xs-400 text-grey-500">
                  {leadTaskData.leadTask.createdBy.fullName}
                </span>
              </span>
            </div>
          )}
        </div>
      }
      onClose={handleClose}
    >
      <Form
        onFieldsChange={() => setformChanged(true)}
        form={form}
        onFinish={handleSave}
        layout="vertical"
      >
        {leadTaskData.mode === "EDIT" && (
          <MarkComplete markedCompleted={markedCompleted} setmarkedCompleted={setmarkedCompleted} />
        )}
        <div className="flex-row-centered w-full items-center gap-2">
          <Form.Item
            label="Task Type"
            rules={[{ required: true, message: "Please select the task type" }]}
            name="taskType"
            className="w-full"
          >
            <Select
              name="taskType"
              placeholder="Select Task Type"
              options={taskStatus}
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            className="w-full"
            name="leadOpportunityId"
            rules={[{ required: true, message: "Please select an opportunity" }]}
            label="Opportunity"
          >
            <Select
              placeholder="Select an opportunity"
              className="w-full"
              name="leadOpportunityId"
              options={
                opportunities &&
                opportunities.length > 0 &&
                opportunities.map(item => ({
                  label: handleOpportunityName(item).substring(0, 30),
                  value: +item.id,
                }))
              }
            />
          </Form.Item>
        </div>
        <Form.Item
          rules={[{ required: true, message: "Please enter task title", whitespace: true }]}
          label="Title"
          className="w-full"
          name="title"
        >
          <Input name="title" placeholder="Enter task title" className="w-full" />
        </Form.Item>
        <Form.Item
          rules={[{ required: true, message: "Please enter task description", whitespace: true }]}
          label="Description"
          className="w-full"
          name="description"
        >
          <Input.TextArea
            className="w-full"
            placeholder="Enter task description"
            name="description"
          />
        </Form.Item>

        <UserSelector
          multiple={false}
          showAll={false}
          validation
          item={{ name: "Assigned To" }}
          errorMessage="Please assign this task"
          name="assignedTo"
          handleUsersSelection={value => setassignedUsers(value)}
        />
        <div className="flex-row-centered w-full items-center gap-2">
          <Form.Item
            rules={[{ required: true, message: "Please select the task due date" }]}
            label="Due Date"
            name="dueDate"
            className="w-full"
          >
            <DatePicker
              disabledDate={date => date.isBefore(dayjs(), "day")}
              format="MM/DD/YYYY"
              name="dueDate"
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            name="dueTime"
            label="Due Time"
            className="w-full"
            rules={[{ required: true, message: "Please select the task due time" }]}
          >
            <TimePicker format="hh:mm A" name="dueTime" className="w-full" />
          </Form.Item>
        </div>
        <p className="mt-1">
          Current timezone : <b>{timeZoneLabel}</b>
        </p>

        <div className="mt-5 w-48">
          <SuggestedAction
            suggestedActionItemClick={() => handleLinkClick(leadTaskData?.selectedLead?.id)}
          />
        </div>
        {/* <Form.Item className="w-full" label="Reminder">
          <Select className="w-full" />
        </Form.Item> */}
      </Form>
    </Drawer>
  );
};

const MarkComplete = ({ markedCompleted, setmarkedCompleted }) => {
  return (
    <div className="p-3 mb-3 rounded-lg border border-solid border-success-300 bg-success-50">
      <Checkbox checked={markedCompleted} onChange={e => setmarkedCompleted(e.target.checked)}>
        Mark as complete
      </Checkbox>
      {markedCompleted && (
        <div className="flex-col-centered mt-6 gap-5">
          <div className="flex-row-centered w-full items-center gap-2">
            <Form.Item
              rules={[{ required: true, message: "Please select the task completion date" }]}
              label="Completion Date"
              name="completionDate"
              className="w-full"
            >
              <DatePicker
                disabledDate={date => date.isAfter(dayjs())}
                name="completionDate"
                className="w-full"
                format="MM/DD/YYYY"
              />
            </Form.Item>
            <Form.Item
              name="completionTime"
              label="Completion Time"
              className="w-full"
              rules={[{ required: true, message: "Please select the task completion time" }]}
            >
              <TimePicker format="hh:mm A" name="completionTime" className="w-full" />
            </Form.Item>
          </div>
          <Form.Item
            name="completionNote"
            label="Completion Note"
            rules={[
              { required: true, message: "Please enter the completion note", whitespace: true },
            ]}
          >
            <Input.TextArea placeholder="Enter the completion note" name="completionNote" />
          </Form.Item>
        </div>
      )}
    </div>
  );
};
